/**
 * Mega Menu JavaScript
 * Dawn Theme 12.0.0 Compatible
 */

class MegaMenu {
  constructor() {
    this.menuItems = document.querySelectorAll('.mega-menu__item');
    this.hoverDelay = 150;
    this.hoverTimeout = null;
    this.activeItem = null;

    this.init();
  }

  init() {
    if (this.menuItems.length === 0) return;

    this.bindDesktopEvents();
    this.bindKeyboardEvents();
    this.bindScrollEvents();
  }

  bindScrollEvents() {
    // Make header sticky only when user scrolls while dropdown is open
    window.addEventListener('scroll', () => {
      if (this.activeItem && this.headerSection) {
        // Enable sticky mode on first scroll with dropdown open
        if (!this.stickyEnabled) {
          this.stickyEnabled = true;
          this.headerSection.classList.add('mega-menu-sticky');
          document.body.classList.add('mega-menu-sticky-active');
        }

        // Update dropdown position
        const dropdown = this.activeItem.querySelector('.mega-menu__dropdown');
        const menuBar = this.activeItem.closest('.mega-menu');
        if (dropdown && menuBar) {
          const menuRect = menuBar.getBoundingClientRect();
          dropdown.style.top = Math.max(0, menuRect.bottom) + 'px';
        }
      }
    }, { passive: true });
  }

  bindDesktopEvents() {
    this.menuItems.forEach(item => {
      const link = item.querySelector('.mega-menu__link');
      const dropdown = item.querySelector('.mega-menu__dropdown');

      if (!dropdown) return;

      // Mouse enter with delay
      item.addEventListener('mouseenter', () => {
        clearTimeout(this.hoverTimeout);
        this.hoverTimeout = setTimeout(() => {
          this.openDropdown(item);
        }, this.hoverDelay);
      });

      // Mouse leave with delay
      item.addEventListener('mouseleave', () => {
        clearTimeout(this.hoverTimeout);
        this.hoverTimeout = setTimeout(() => {
          this.closeDropdown(item);
        }, this.hoverDelay);
      });

      // Click to toggle (for accessibility)
      link.addEventListener('click', (e) => {
        if (dropdown) {
          e.preventDefault();
          if (item.classList.contains('mega-menu__item--active')) {
            this.closeDropdown(item);
          } else {
            this.openDropdown(item);
          }
        }
      });
    });

    // Close when clicking outside
    document.addEventListener('click', (e) => {
      if (!e.target.closest('.mega-menu')) {
        this.closeAllDropdowns();
      }
    });
  }

  bindKeyboardEvents() {
    this.menuItems.forEach(item => {
      const link = item.querySelector('.mega-menu__link');
      const dropdown = item.querySelector('.mega-menu__dropdown');

      // Enter/Space to toggle
      link.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          if (dropdown) {
            e.preventDefault();
            if (item.classList.contains('mega-menu__item--active')) {
              this.closeDropdown(item);
            } else {
              this.openDropdown(item);
              // Focus first link in dropdown
              const firstLink = dropdown.querySelector('a');
              if (firstLink) {
                setTimeout(() => firstLink.focus(), 100);
              }
            }
          }
        }

        // Arrow down to open and enter dropdown
        if (e.key === 'ArrowDown' && dropdown) {
          e.preventDefault();
          this.openDropdown(item);
          const firstLink = dropdown.querySelector('a');
          if (firstLink) {
            setTimeout(() => firstLink.focus(), 100);
          }
        }
      });

      // Escape to close
      item.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') {
          this.closeDropdown(item);
          link.focus();
        }
      });
    });

    // Arrow navigation between top-level items
    document.addEventListener('keydown', (e) => {
      if (!e.target.closest('.mega-menu')) return;

      const currentItem = e.target.closest('.mega-menu__item');
      if (!currentItem) return;

      const items = Array.from(this.menuItems);
      const currentIndex = items.indexOf(currentItem);

      if (e.key === 'ArrowRight') {
        e.preventDefault();
        const nextIndex = (currentIndex + 1) % items.length;
        const nextLink = items[nextIndex].querySelector('.mega-menu__link');
        this.closeAllDropdowns();
        nextLink.focus();
      }

      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        const prevIndex = (currentIndex - 1 + items.length) % items.length;
        const prevLink = items[prevIndex].querySelector('.mega-menu__link');
        this.closeAllDropdowns();
        prevLink.focus();
      }
    });
  }

  openDropdown(item) {
    this.closeAllDropdowns();
    item.classList.add('mega-menu__item--active');

    const link = item.querySelector('.mega-menu__link');
    const dropdown = item.querySelector('.mega-menu__dropdown');

    link.setAttribute('aria-expanded', 'true');
    if (dropdown) {
      dropdown.setAttribute('aria-hidden', 'false');

      // Position dropdown below the menu bar
      const menuBar = item.closest('.mega-menu');
      if (menuBar) {
        const menuRect = menuBar.getBoundingClientRect();
        dropdown.style.top = menuRect.bottom + 'px';
      }

      // Store header height for potential sticky behavior on scroll
      const headerSection = document.querySelector('.section-header');
      if (headerSection) {
        const headerHeight = headerSection.offsetHeight;
        document.documentElement.style.setProperty('--header-height', headerHeight + 'px');
        this.headerSection = headerSection;
        this.stickyEnabled = false;
      }
    }

    this.activeItem = item;
  }

  closeDropdown(item) {
    item.classList.remove('mega-menu__item--active');

    const link = item.querySelector('.mega-menu__link');
    const dropdown = item.querySelector('.mega-menu__dropdown');

    link.setAttribute('aria-expanded', 'false');
    if (dropdown) {
      dropdown.setAttribute('aria-hidden', 'true');
    }

    // Remove sticky from header
    if (this.headerSection) {
      this.headerSection.classList.remove('mega-menu-sticky');
      document.body.classList.remove('mega-menu-sticky-active');
      this.stickyEnabled = false;
    }

    if (this.activeItem === item) {
      this.activeItem = null;
    }
  }

  closeAllDropdowns() {
    this.menuItems.forEach(item => {
      this.closeDropdown(item);
    });
  }
}

/**
 * Mobile Mega Menu
 */
class MegaMenuMobile {
  constructor() {
    this.drawer = document.querySelector('.mega-menu-mobile__drawer');
    this.backdrop = document.querySelector('.mega-menu-mobile__backdrop');
    this.openButton = document.querySelector('.mega-menu-mobile__toggle');
    this.closeButton = document.querySelector('.mega-menu-mobile__close');
    this.accordionToggles = document.querySelectorAll('.mega-menu-mobile__accordion-toggle');

    this.isOpen = false;
    this.focusableElements = [];
    this.firstFocusable = null;
    this.lastFocusable = null;

    this.init();
  }

  init() {
    if (!this.drawer || !this.openButton) return;

    this.bindEvents();
    this.setupFocusTrap();
  }

  bindEvents() {
    // Open drawer
    this.openButton.addEventListener('click', () => {
      this.open();
    });

    // Close drawer
    if (this.closeButton) {
      this.closeButton.addEventListener('click', () => {
        this.close();
      });
    }

    // Close on backdrop click
    if (this.backdrop) {
      this.backdrop.addEventListener('click', () => {
        this.close();
      });
    }

    // Escape to close
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.isOpen) {
        this.close();
      }
    });

    // Accordion toggles
    this.accordionToggles.forEach(toggle => {
      toggle.addEventListener('click', () => {
        this.toggleAccordion(toggle);
      });

      toggle.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          this.toggleAccordion(toggle);
        }
      });
    });
  }

  setupFocusTrap() {
    this.focusableElements = this.drawer.querySelectorAll(
      'a[href], button:not([disabled]), textarea, input, select, [tabindex]:not([tabindex="-1"])'
    );

    if (this.focusableElements.length > 0) {
      this.firstFocusable = this.focusableElements[0];
      this.lastFocusable = this.focusableElements[this.focusableElements.length - 1];
    }

    this.drawer.addEventListener('keydown', (e) => {
      if (e.key !== 'Tab' || !this.isOpen) return;

      if (e.shiftKey) {
        if (document.activeElement === this.firstFocusable) {
          e.preventDefault();
          this.lastFocusable.focus();
        }
      } else {
        if (document.activeElement === this.lastFocusable) {
          e.preventDefault();
          this.firstFocusable.focus();
        }
      }
    });
  }

  open() {
    this.isOpen = true;
    this.drawer.classList.add('mega-menu-mobile__drawer--open');

    if (this.backdrop) {
      this.backdrop.classList.add('mega-menu-mobile__backdrop--visible');
    }

    document.body.classList.add('mega-menu-mobile--open');

    this.openButton.setAttribute('aria-expanded', 'true');
    this.drawer.setAttribute('aria-hidden', 'false');

    // Focus close button after transition
    setTimeout(() => {
      if (this.closeButton) {
        this.closeButton.focus();
      }
    }, 100);

    // Announce to screen readers
    this.announceToScreenReader('Navigation menu opened');
  }

  close() {
    this.isOpen = false;
    this.drawer.classList.remove('mega-menu-mobile__drawer--open');

    if (this.backdrop) {
      this.backdrop.classList.remove('mega-menu-mobile__backdrop--visible');
    }

    document.body.classList.remove('mega-menu-mobile--open');

    this.openButton.setAttribute('aria-expanded', 'false');
    this.drawer.setAttribute('aria-hidden', 'true');

    // Return focus to open button
    this.openButton.focus();

    // Announce to screen readers
    this.announceToScreenReader('Navigation menu closed');
  }

  toggleAccordion(toggle) {
    const item = toggle.closest('.mega-menu-mobile__item');
    const isExpanded = item.classList.contains('mega-menu-mobile__item--expanded');

    // Close all other accordions
    document.querySelectorAll('.mega-menu-mobile__item--expanded').forEach(openItem => {
      if (openItem !== item) {
        openItem.classList.remove('mega-menu-mobile__item--expanded');
        const otherToggle = openItem.querySelector('.mega-menu-mobile__accordion-toggle');
        if (otherToggle) {
          otherToggle.setAttribute('aria-expanded', 'false');
        }
      }
    });

    // Toggle current accordion
    if (isExpanded) {
      item.classList.remove('mega-menu-mobile__item--expanded');
      toggle.setAttribute('aria-expanded', 'false');
    } else {
      item.classList.add('mega-menu-mobile__item--expanded');
      toggle.setAttribute('aria-expanded', 'true');
    }
  }

  announceToScreenReader(message) {
    const announcement = document.createElement('div');
    announcement.setAttribute('role', 'status');
    announcement.setAttribute('aria-live', 'polite');
    announcement.setAttribute('aria-atomic', 'true');
    announcement.classList.add('mega-menu-sr-only');
    announcement.textContent = message;
    document.body.appendChild(announcement);

    setTimeout(() => {
      announcement.remove();
    }, 1000);
  }
}

// Initialize when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
  new MegaMenu();
  new MegaMenuMobile();
});

// Also initialize on Shopify section load (for theme editor)
if (typeof Shopify !== 'undefined' && Shopify.designMode) {
  document.addEventListener('shopify:section:load', () => {
    new MegaMenu();
    new MegaMenuMobile();
  });
}
